import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate, Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { Modal } from 'react-responsive-modal';
import 'react-responsive-modal/styles.css';
import { message } from 'antd';

import { MailboxContext } from '../../context/Mailbox';
import { AuthContext } from '../../context/AuthProvider';
import ModalEditMailbox from '../ModalEditMailbox';
import PasswordInput, { passwordRules } from '../PasswordInput';
import { WEBMAIL_URL } from '../SiteHeader';
import '../SiteChrome.css';
import './Mailbox.css';

const freeDomain 	= 'diamta.com';
const freeDomainMax = 5;
const localPartRegex = /^[a-z0-9][a-z0-9._-]{0,63}$/;

const Icon = ({ children, size = 20 }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const MailIcon = (props) => <Icon {...props}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></Icon>;

const Mailbox = () => {
	const navigate = useNavigate();
	const { getUser, isAuthenticated, setUser } = useContext( AuthContext );
	const { getAllMailboxes, getAllDomains, mailboxExists, createMailbox, imapLogin } = useContext( MailboxContext );
	const userEmail = ( getUser() || {} ).userEmail || '';

	const [ mailboxes, setMailboxes ] 	= useState( [] );
	const [ domains, setDomains ] 		= useState( [] );
	const [ loading, setLoading ] 		= useState( true );
	const [ search, setSearch ] 		= useState( '' );
	const [ navOpen, setNavOpen ] 		= useState( false );

	// create form
	const [ showCreate, setShowCreate ] = useState( new URLSearchParams( window.location.search ).get( 'new' ) === '1' );	// /mailbox/list?new=1 opens the form
	const [ localPart, setLocalPart ] 	= useState( '' );
	const [ domain, setDomain ] 		= useState( freeDomain );
	const [ password, setPassword ] 	= useState( '' );
	const [ errors, setErrors ] 		= useState( {} );
	const [ creating, setCreating ] 	= useState( false );

	const [ opening, setOpening ] 		= useState( '' );	// mailbox being opened in webmail
	const [ editing, setEditing ] 		= useState( null );	// mailbox in the edit modal

	const loadMailboxes = async () => {
		const boxes = await getAllMailboxes();
		setMailboxes( boxes );
		setLoading( false );
	};

	useEffect( () => {
		if( !isAuthenticated() ){
			navigate( '/login' );
			return;
		}
		loadMailboxes();
		getAllDomains().then( setDomains );
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [] );

	const domainOptions = useMemo( () => {
		const own = domains.map( d => d.domain ).filter( d => d !== freeDomain );
		return [ freeDomain, ...own ];
	}, [ domains ] );

	const freeUsed = mailboxes.filter( m => m.email.endsWith( '@' + freeDomain ) ).length;
	const freeLeft = Math.max( 0, freeDomainMax - freeUsed );

	const visibleMailboxes = mailboxes.filter( m => m.email.includes( search.trim().toLowerCase() ) );

	const logOut = () => {
		setUser( null );
		navigate( '/login' );
	};

	// Open the webmail, already logged in
	const handleOpenMailbox = async ( mailbox ) => {
		const tab = window.open( 'about:blank', '_blank' );	// open now, before the await, so popup blockers allow it
		setOpening( mailbox.email );
		const rep = await imapLogin( mailbox );
		if( rep && rep.ok === true ){
			if( tab ) tab.location.href = WEBMAIL_URL;
			else window.location.href = WEBMAIL_URL;
		}
		else{
			if( tab ) tab.close();
			message.error( 'We could not open your mailbox. Please try again later.' );
		}
		setOpening( '' );
	};

	const resetCreateForm = () => {
		setLocalPart( '' );
		setPassword( '' );
		setErrors( {} );
	};

	const validate = () => {
		const next = {};
		if( !localPart )
			next.local = 'Enter the part before the @.';
		else if( localPart.includes( '@' ) )
			next.local = 'Enter only the part before the @. The domain is chosen on the right.';
		else if( !localPartRegex.test( localPart ) )
			next.local = 'Use letters, numbers, dots, dashes or underscores, starting with a letter or number.';
		if( !passwordRules.every( r => r.test( password ) ) )
			next.password = 'Choose a password that meets all the rules below.';
		else if( password.length > 100 )
			next.password = 'Your password can be at most 100 characters long.';
		if( domain === freeDomain && freeLeft === 0 )
			next.local = 'You have used all ' + freeDomainMax + ' free addresses @' + freeDomain + '.';
		return next;
	};

	const handleCreate = async ( e ) => {
		e.preventDefault();
		const next = validate();
		setErrors( next );
		if( Object.keys( next ).length )
			return;

		const email = localPart + '@' + domain;
		setCreating( true );
		const exists = await mailboxExists( email );
		if( exists === true ){
			setErrors( { local: email + ' is already taken. Try another address.' } );
			setCreating( false );
			return;
		}
		if( exists === null ){
			setCreating( false );
			return;
		}

		const rep = await createMailbox( { email, password } );
		setCreating( false );
		if( rep ){
			message.success( email + ' is ready.' );
			resetCreateForm();
			setShowCreate( false );
			loadMailboxes();
		}
	};

	const closeEdit = ( changed ) => {
		setEditing( null );
		if( changed )
			loadMailboxes();
	};

	const navLinks = (
		<>
			<Link to="/mailbox/list" className="dm-app-nav-link is-active" aria-current="page" onClick={ () => setNavOpen( false ) }>
				<MailIcon size={18} />Email manager
			</Link>
			<a href={ WEBMAIL_URL } className="dm-app-nav-link">
				<Icon size={18}><path d="M4 4h16v16H4z" /><path d="M4 9h16M9 9v11" /></Icon>Webmail
			</a>
			<HashLink smooth to="/home#setup" className="dm-app-nav-link">
				<Icon size={18}><rect x="7" y="2" width="10" height="20" rx="2" /><path d="M11 18h2" /></Icon>Email app setup
			</HashLink>
			<HashLink smooth to="/home#pricing" className="dm-app-nav-link">
				<Icon size={18}><path d="M20 12l-8 8-9-9V3h8z" /><circle cx="7.5" cy="7.5" r="1.5" /></Icon>Plans &amp; pricing
			</HashLink>
			<a href="mailto:info@diamta.com?subject=Add%20a%20domain%20to%20Diamta%20Mail" className="dm-app-nav-link">
				<Icon size={18}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></Icon>Add a domain
			</a>
			<Link to="/contact" className="dm-app-nav-link">
				<Icon size={18}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></Icon>Contact us
			</Link>
		</>
	);

	return (
		<div className="dm-site dm-app">
			{/* Sidebar */}
			<aside className={ 'dm-app-side' + ( navOpen ? ' is-open' : '' ) } aria-label="Main">
				<Link to="/home" className="dm-app-brand">
					<svg width="34" height="34" viewBox="0 0 40 40" aria-hidden="true">
						<path d="M20 3 L37 20 L20 37 L3 20 Z" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
						<text x="20" y="25.5" textAnchor="middle" fontSize="15" fontWeight="800" fill="currentColor">@</text>
					</svg>
					<span className="dm-app-brand-text">
						<span>Diamta Mail</span>
						<small>Free &amp; Pro Email</small>
					</span>
				</Link>
				<nav className="dm-app-nav">{ navLinks }</nav>
				<div className="dm-app-side-foot">
					<span className="dm-app-side-user" title={ userEmail }>{ userEmail }</span>
					<button type="button" className="dm-app-nav-link dm-app-logout" onClick={ logOut }>
						<Icon size={18}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></Icon>Log out
					</button>
				</div>
			</aside>
			{ navOpen && <button type="button" className="dm-app-scrim" aria-label="Close menu" onClick={ () => setNavOpen( false ) } /> }

			<div className="dm-app-main">
				{/* Top bar */}
				<header className="dm-app-top">
					<button type="button" className="dm-app-menu" aria-label="Open menu" aria-expanded={ navOpen } onClick={ () => setNavOpen( true ) }>
						<Icon size={22}><path d="M4 7h16M4 12h16M4 17h16" /></Icon>
					</button>
					<div className="dm-app-search">
						<label htmlFor="dm-search" className="dm-visually-hidden">Search your mailboxes</label>
						<Icon size={18}><circle cx="11" cy="11" r="7" /><path d="M20 20l-4-4" /></Icon>
						<input id="dm-search" type="search" placeholder="Search your mailboxes" value={ search } onChange={ e => setSearch( e.target.value ) } />
					</div>
					<div className="dm-app-user">
						<span className="dm-app-avatar" aria-hidden="true">{ ( userEmail[ 0 ] || '?' ).toUpperCase() }</span>
						<span className="dm-app-user-email">{ userEmail }</span>
					</div>
				</header>

				<main className="dm-app-content">
					<div className="dm-app-head">
						<div>
							<h1>Email manager</h1>
							<p className="dm-app-sub">
								{ loading ? 'Loading your mailboxes…' : 'You have ' + mailboxes.length + ' email address' + ( mailboxes.length === 1 ? '' : 'es' ) + '.' }
							</p>
						</div>
						{ !showCreate &&
							<button type="button" className="dm-btn dm-btn-primary" onClick={ () => setShowCreate( true ) }>
								<Icon size={18}><path d="M12 5v14M5 12h14" /></Icon>&nbsp;New mailbox
							</button>
						}
					</div>

					{/* Plan usage */}
					<div className="dm-app-usage">
						<div className="dm-app-usage-text">
							<b>Free addresses @{ freeDomain }:</b> { freeUsed } of { freeDomainMax } used
							<div className="dm-app-meter" aria-hidden="true"><span style={{ width: Math.min( 100, freeUsed / freeDomainMax * 100 ) + '%' }} /></div>
						</div>
						<p>Want addresses on your own domain, without ads? <HashLink smooth to="/home#pricing">See Business plans</HashLink></p>
					</div>

					{/* Create */}
					{ showCreate &&
						<section className="dm-app-card dm-app-create" aria-labelledby="dm-create-title">
							<div className="dm-app-card-head">
								<h2 id="dm-create-title">New mailbox</h2>
								<button type="button" className="dm-app-icon-btn" aria-label="Close" onClick={ () => { setShowCreate( false ); resetCreateForm(); } }>
									<Icon size={20}><path d="M6 6l12 12M18 6L6 18" /></Icon>
								</button>
							</div>
							<form onSubmit={ handleCreate } noValidate>
								<div className="dm-field">
									<label htmlFor="dm-local">Email address</label>
									<div className="dm-address">
										<input
											id				= "dm-local"
											className		= "dm-input"
											value			= { localPart }
											onChange		= { e => setLocalPart( e.target.value.toLowerCase().trim() ) }
											placeholder		= "yourname"
											autoComplete	= "off"
											autoCapitalize	= "off"
											spellCheck		= { false }
											aria-invalid	= { !!errors.local || undefined }
											aria-describedby = { errors.local ? 'dm-local-error' : undefined }
										/>
										<span className="dm-at" aria-hidden="true">@</span>
										<label htmlFor="dm-domain" className="dm-visually-hidden">Domain</label>
										<select id="dm-domain" className="dm-input" value={ domain } onChange={ e => setDomain( e.target.value ) }>
											{ domainOptions.map( d => <option key={ d } value={ d }>{ d }</option> ) }
										</select>
									</div>
									{ errors.local && <p className="dm-error" id="dm-local-error">{ errors.local }</p> }
									{ !errors.local && domain === freeDomain &&
										<p className="dm-help">{ freeLeft } free address{ freeLeft === 1 ? '' : 'es' } left @{ freeDomain }.</p>
									}
								</div>

								<div className="dm-field">
									<label htmlFor="dm-new-password">Password</label>
									<PasswordInput
										id				= "dm-new-password"
										className		= "dm-input"
										value			= { password }
										onChange		= { setPassword }
										placeholder		= "Choose a password"
										autoComplete	= "new-password"
										invalid			= { !!errors.password }
										describedBy		= { errors.password ? 'dm-password-error' : undefined }
										suggest
										showRules
									/>
									{ errors.password && <p className="dm-error" id="dm-password-error">{ errors.password }</p> }
								</div>

								<div className="dm-app-form-actions">
									<button type="submit" className="dm-btn dm-btn-primary" disabled={ creating }>
										{ creating ? 'Creating…' : 'Create mailbox' }
									</button>
									<button type="button" className="dm-btn dm-btn-outline" onClick={ () => { setShowCreate( false ); resetCreateForm(); } }>Cancel</button>
								</div>
							</form>
						</section>
					}

					{/* List */}
					<section className="dm-app-card" aria-labelledby="dm-list-title">
						<div className="dm-app-card-head">
							<h2 id="dm-list-title">Your mailboxes</h2>
						</div>
						{ loading &&
							<p className="dm-app-empty">Loading…</p>
						}
						{ !loading && mailboxes.length === 0 &&
							<div className="dm-app-empty">
								<p>You don't have any mailboxes yet.</p>
								{ !showCreate && <button type="button" className="dm-btn dm-btn-primary" onClick={ () => setShowCreate( true ) }>Create your first mailbox</button> }
							</div>
						}
						{ !loading && mailboxes.length > 0 && visibleMailboxes.length === 0 &&
							<p className="dm-app-empty">No mailbox matches “{ search }”.</p>
						}
						{ visibleMailboxes.length > 0 &&
							<ul className="dm-mb-list">
								{ visibleMailboxes.map( mailbox => {
									const isFree = mailbox.email.endsWith( '@' + freeDomain );
									return (
										<li className="dm-mb-row" key={ mailbox.email }>
											<span className="dm-mb-icon"><MailIcon size={18} /></span>
											<span className="dm-mb-text">
												<span className="dm-mb-email">{ mailbox.email }</span>
												<span className={ 'dm-mb-tag' + ( isFree ? '' : ' is-own' ) }>{ isFree ? 'Free' : 'Your domain' }</span>
											</span>
											<span className="dm-mb-actions">
												<button type="button" className="dm-btn dm-btn-primary dm-btn-sm" onClick={ () => handleOpenMailbox( mailbox ) } disabled={ opening === mailbox.email }>
													{ opening === mailbox.email ? 'Opening…' : 'Open mailbox' }
												</button>
												<button type="button" className="dm-btn dm-btn-outline dm-btn-sm" onClick={ () => setEditing( mailbox ) } aria-label={ 'Edit ' + mailbox.email }>
													Edit
												</button>
											</span>
										</li>
									);
								} ) }
							</ul>
						}
					</section>
				</main>
			</div>

			<Modal open={ !!editing } onClose={ () => closeEdit( false ) } center classNames={{ modal: 'dm-modal' }}>
				{ editing && <ModalEditMailbox mailbox={ editing } onClose={ closeEdit } /> }
			</Modal>
		</div>
	);
};

export default Mailbox;
